import { Header, Icon, Modal } from "semantic-ui-react";
import { TShoot } from "../types";
import React, { useCallback, useEffect, useState } from "react";
import Shoots from "../../api/resources/Gardener/Shoots";
import { useSelector } from "react-redux";
import { getGardenerDomain } from "../reducer/selectors";
import { toast } from "react-toastify";

type EnableHAControlPlaneModalProps = {
  shoot: TShoot;
  onClose: (prevState: boolean) => void;
};

export default function EnableHAControlPlaneModal({
  shoot,
  onClose,
}: EnableHAControlPlaneModalProps) {
  const [isShootReady, setIsShootReady] = useState<boolean>(false);
  const gardenDomain = useSelector(getGardenerDomain);

  const closeModal = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const enableHaControlPlane = useCallback(() => {
    Shoots.enableHaControlPlane({
      region: shoot.spec.region,
      project_id: shoot.project_id,
      gardenDomain: gardenDomain,
      name: shoot.metadata.name,
    })
      .then(() => {
        toast.success("Started Control Plane upgrade");
      })
      .catch((error) => {
        toast.error(error.response.data.error.message);
      })
      .finally(() => {
        closeModal();
      });
  }, [
    closeModal,
    gardenDomain,
    shoot.metadata.name,
    shoot.project_id,
    shoot.spec.region,
  ]);

  useEffect(() => {
    if (
      !shoot.status.hibernated &&
      shoot.status.lastOperation?.state === "Succeeded"
    ) {
      setIsShootReady(true);
    }
  }, [shoot]);

  return (
    <Modal
      className={"max-width-600"}
      centered={true}
      dimmer={"blurring"}
      open={true}
      onClose={closeModal}
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      <Header>
        <Header.Content className="flex vcenter">
          <div className="sidebar_svg">
            <Icon name="archive" />
          </div>
          <div className="color-black content padding-left-half">
            Enable Highly Available Control Plane{" "}
            {shoot.metadata.name && `for shoot: ${shoot.metadata.name}`}
          </div>
        </Header.Content>
      </Header>
      <Modal.Content>
        <div>
          <Header size="small" color="grey" style={{ borderColor: "grey" }}>
            Warning:
          </Header>
          <p>
            This action will enable Highly Available Control Plane for the
            Shoot.
          </p>
          <p>
            When enabled in regions that support{" "}
            <span className="color-orange">
              Three or more Availability Zones
            </span>
            , Control Plane components will be replicated across all
            Availability Zones. This ensures that in case of an Availability
            Zone outage, the Control Plane for the cluster remains available.
          </p>
          <p>
            In Regions that support only{" "}
            <span className="color-orange">Single Availability Zone</span>,
            Control Plane components will be replicated accross multiple Nodes
            to prevent any outages.
          </p>
          <p>
            This option acts independently from Availability Zones selected for
            Worker Groups, allowing you to choose any number of Availability
            Zones for Worker Groups.
          </p>
          {isShootReady ? (
            <p>Click "Enable HA Control Plane" to proceed.</p>
          ) : (
            <p className="padding-20 bg-light-grey">
              Note: The Shoot is currently running an operation. Please wait
              until it finishes.
            </p>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <button
          className="float-left button button--bordered"
          onClick={closeModal}
        >
          <span>Back</span>
        </button>
        <button
          className={`float-right button button--icon__left button--red ${!isShootReady && "button--disabled"}`}
          onClick={enableHaControlPlane}
          disabled={!isShootReady}
        >
          <Icon name="th" />
          <span>Enable HA Control Plane</span>
        </button>
      </Modal.Actions>
    </Modal>
  );
}
